import * as React from 'react';
import type { ComboboxGroupedItem } from './types';

/**
 * Converts supported primitive values into display strings.
 */
function getComboboxStringValue(value: unknown) {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }

  return null;
}

/**
 * Returns whether the combobox currently has a selected value.
 * Supports both single-value and multiple-value selection shapes.
 */
export function hasSelectedComboboxValue(selectedValue: unknown) {
  if (Array.isArray(selectedValue)) {
    return selectedValue.length > 0;
  }

  return selectedValue != null;
}

/**
 * Copies text to the clipboard using the async Clipboard API when available,
 * falling back to a DOM execCommand approach for older browsers.
 * Returns `true` when the copy operation succeeds.
 */
export async function copyTextToClipboard(text: string) {
  if (text === '') {
    return false;
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);
  textarea.select();

  const execCommand = Reflect.get(document, 'execCommand') as
    | ((commandId: string) => boolean)
    | undefined;
  const didCopy = execCommand?.call(document, 'copy') ?? false;

  textarea.remove();

  return didCopy;
}

/**
 * Resolves the fallback label used by the wrappers for items and chips.
 */
export function getComboboxItemLabel<Value>(
  item: Value,
  itemToStringLabel?: (item: Value) => string,
) {
  if (itemToStringLabel) {
    return itemToStringLabel(item);
  }

  const primitiveValue = getComboboxStringValue(item);

  if (primitiveValue != null) {
    return primitiveValue;
  }

  if (item && typeof item === 'object') {
    if ('label' in item) {
      const label = getComboboxStringValue(item.label);

      if (label != null) {
        return label;
      }
    }

    if ('value' in item) {
      const value = getComboboxStringValue(item.value);

      if (value != null) {
        return value;
      }
    }
  }

  return '';
}

/**
 * Resolves a stable React key from the configured string value or a common `value` field.
 */
export function getComboboxItemKey<Value>(
  item: Value,
  index: number,
  itemToStringValue?: (item: Value) => string,
) {
  if (itemToStringValue) {
    return itemToStringValue(item);
  }

  if (item && typeof item === 'object' && 'value' in item) {
    const value = getComboboxStringValue(item.value);

    if (value != null) {
      return value;
    }
  }

  return index;
}

/**
 * Resolves a stable React key for a combobox group. Uses the group's
 * primitive `value` when it's a string or number, otherwise falls back to the
 * provided index.
 */
export function getComboboxGroupKey<Value>(
  group: ComboboxGroupedItem<Value>,
  index: number,
): React.Key {
  return typeof group.value === 'string' || typeof group.value === 'number'
    ? group.value
    : index;
}

export function shouldAnimateComboboxIndicator(itemCount: number) {
  // the animation competes with browser rendering of items in the popover when
  // there are many items, so we disable it in that case
  return itemCount < 100;
}
